import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { login } from '@/api/user';
import { UserLoginParams } from '@/types/user';
import { Button, Tooltip } from 'antd';
import Title from 'antd/es/typography/Title';

const LoginPage: React.FC<{
  isComponent?: boolean;
  setCurrentToken?: (token: string) => void;
}> = ({ isComponent = false, setCurrentToken }) => {
  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');
  const [password, setPassword] = useState('');
  // 当前登录状态
  const [isLogin, setIsLogin] = useState(false);
  const navigate = useNavigate();

  const mutation = useMutation({
    mutationFn: (data: UserLoginParams) => login(data),
    onSuccess: (data) => {
      localStorage.setItem('userEmail', email);
      localStorage.setItem(email, data?.data.userToken);
      setIsLogin(true);
      if (setCurrentToken) {
        setCurrentToken(data?.data.userToken);
      }
      if (isComponent) {
        return true;
      }
      navigate('/img/upload');
    },
    onError: (error) => {
      console.error('Login failed:', error);
      alert('登录失败，请检查邮箱和密码');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    mutation.mutate({ email, password });
  };

  useEffect(() => {
    const userEmail = localStorage.getItem('userEmail');
    if (userEmail) {
      setEmail(userEmail);
      setToken(localStorage.getItem(userEmail) || '');
      setIsLogin(true);
      if (setCurrentToken) {
        setCurrentToken(localStorage.getItem(userEmail) as string);
      }
    }
  }, []);

  const containerStyle = isComponent
    ? {
        ...styles.container,
        minHeight: 'auto', // Remove full screen height
        padding: '10px', // Reduce padding
        backgroundColor: 'transparent', // Remove background
      }
    : styles.container;

  const handleLogout = () => {
    localStorage.removeItem('userEmail');
    localStorage.removeItem('tokens');
    localStorage.removeItem(email);
    setIsLogin(false);
    if (setCurrentToken) {
      setCurrentToken('');
    }
  };

  if (isLogin) {
    return (
      <div style={styles.loggedInContainer}>
        <div style={styles.loggedInInfo}>
          <Title level={4}>当前 Supp 登录信息</Title>
          <div style={styles.infoItem}>
            <span style={styles.label}>当前用户:</span>
            <span style={styles.value}>{email}</span>
          </div>
          <div style={styles.infoItem}>
            <span style={styles.label}>当前Token:</span>
            <Tooltip title={token} placement="bottom">
              <span style={styles.value}>{token.slice(0, 15)}...</span>
            </Tooltip>
          </div>
        </div>
        <Button type="primary" danger onClick={handleLogout}>
          退出登录
        </Button>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <div style={styles.card}>
        <h1 style={styles.title}>使用 Supp 账号登录</h1>
        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.inputGroup}>
            <label>Email:</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              style={styles.input}
            />
          </div>
          <div style={styles.inputGroup}>
            <label>Password:</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              style={styles.input}
            />
          </div>
          <button type="submit" style={styles.button}>
            登录
          </button>
        </form>
      </div>
    </div>
  );
};

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column' as 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    backgroundColor: '#f0f2f5',
    padding: '20px',
  },
  card: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    padding: '40px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    width: '100%',
    maxWidth: '400px',
  },
  title: {
    fontSize: '24px',
    color: '#1a1a1a',
    marginBottom: '30px',
    textAlign: 'center' as 'center',
  },
  form: {
    display: 'flex',
    flexDirection: 'column' as 'column',
    gap: '20px',
    width: '100%',
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column' as 'column',
    gap: '8px',
  },
  input: {
    padding: '12px',
    fontSize: '14px',
    borderRadius: '6px',
    border: '1px solid #d9d9d9',
    transition: 'all 0.3s',
    ':focus': {
      borderColor: '#40a9ff',
      boxShadow: '0 0 0 2px rgba(24, 144, 255, 0.2)',
    },
  },
  button: {
    padding: '12px 20px',
    fontSize: '16px',
    fontWeight: '500',
    color: '#fff',
    backgroundColor: '#1890ff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'all 0.3s',
    ':hover': {
      backgroundColor: '#40a9ff',
    },
  },
  loggedInContainer: {
    backgroundColor: '#fff',
    padding: '20px',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    width: '100%',
    maxWidth: '600px',
  },
  loggedInInfo: {
    marginBottom: '20px',
  },
  infoItem: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '10px',
  },
  label: {
    fontWeight: '500',
    color: '#666',
    width: '100px',
  },
  value: {
    color: '#1a1a1a',
    wordBreak: 'break-all' as 'break-all',
  },
};

export default LoginPage;
